import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import { FlatList, ListRenderItem } from 'react-native'
import Animated, { LinearTransition } from 'react-native-reanimated'

import styled from 'styled-components/native'

import { AppContext } from '../../context'
import { ConversationItem } from '../../molecules/conversationItem/ConversationItem'
import { Conversation } from '../../ui-models/messages/Conversations'

const ItemSeparatorComponent = styled.View(({ theme }) => ({
  height: '1px',
  backgroundColor: theme.colors.borderDefault,
}))

export type ConversationListProps = {
  conversations: Conversation[]
  onConversationPressed: ({ clientId, externallySelected }: { clientId: string; externallySelected?: boolean }) => void
  scrolledToClientId?: string | null
  selectedClientId: string | null
  setScrolledToClientId: (clientId: string | null) => void
}

const APPROX_ITEM_HEIGHT = 68

export const ConversationList: React.FC<ConversationListProps> = ({
  conversations,
  onConversationPressed,
  scrolledToClientId,
  selectedClientId,
  setScrolledToClientId,
}) => {
  const flatListRef = useRef<FlatList>(null)
  const { shouldShowMessengerDraftsUtility } = useContext(AppContext)

  useEffect(() => {
    if (!shouldShowMessengerDraftsUtility) {
      return
    }
    const index = conversations.findIndex((item) => item.clientId === scrolledToClientId)
    setScrolledToClientId(null)
    if (index !== -1) {
      flatListRef.current?.scrollToIndex({ index, animated: true })
    }
  }, [conversations, setScrolledToClientId, scrolledToClientId, shouldShowMessengerDraftsUtility])

  const renderItem: ListRenderItem<Conversation> = useCallback(
    ({ item }) => (
      <ConversationItem
        conversation={item}
        onPress={() => onConversationPressed({ clientId: item.clientId })}
        isSelected={item.clientId === selectedClientId}
      />
    ),
    [onConversationPressed, selectedClientId],
  )
  const keyExtractor = (item: Conversation) => item.clientId
  const extraData = useMemo(
    () => ({ onConversationPressed, selectedClientId }),
    [onConversationPressed, selectedClientId],
  )
  return (
    <>
      <Animated.FlatList
        ref={flatListRef}
        data={conversations}
        renderItem={renderItem}
        keyExtractor={keyExtractor}
        ItemSeparatorComponent={ItemSeparatorComponent}
        ListFooterComponent={ItemSeparatorComponent}
        scrollEnabled={true}
        extraData={extraData}
        getItemLayout={(_, index) => ({
          length: APPROX_ITEM_HEIGHT,
          offset: APPROX_ITEM_HEIGHT * index,
          index,
        })}
        windowSize={5}
        initialNumToRender={20}
        itemLayoutAnimation={LinearTransition.springify()}
      />
    </>
  )
}
